import {
  canBuyCredits,
  creditTypeConfig,
  getCreditBreakdown,
  getExpiringSoonCreditsCount,
  hasCredits,
} from '@frontend/api-client';
import type { Credit } from '@frontend/api-types';
import { capitalize, convertDateLikeToDateTimeISOString, dateFormatters, dayjs, pluralize } from '@frontend/duck-tape';
import { CoinsIcon } from '@frontend/web-icons';
import { Button, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveFullLedgerReportQuery,
} from '@frontend/web/hooks';
import { useMemo, useState } from 'react';
import { BuyCreditsModal } from '../Payment/BuyCreditsModal';
import { CreditHistoryModal } from './CreditHistoryModal';

type modalKey = 'buy' | 'history';

type MembershipCreditsProps = {
  credits: Credit;
  isNewAccountDetailsPageEnabled: boolean;
  onAccountPress?: () => void;
  onCreditsRefetch?: () => void;
  timeframe?: { end: DateTimeISOString; start: DateTimeISOString };
};

export const MembershipCredits = ({
  credits,
  isNewAccountDetailsPageEnabled,
  onAccountPress,
  onCreditsRefetch,
  timeframe = {
    end: convertDateLikeToDateTimeISOString(dayjs().startOf('minute')),
    start: convertDateLikeToDateTimeISOString(dayjs().startOf('minute').subtract(2, 'month')),
  },
}: MembershipCreditsProps) => {
  const {
    data: ledgerEntries = [],
    isError: ledgerError,
    isLoading: loadingLedger,
    refetch: refetchLedger,
  } = useRetrieveFullLedgerReportQuery({
    end: timeframe.end,
    start: timeframe.start,
  });

  const { data: client } = useRetrieveClientQuery();
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();

  const [modalKey, setModalKey] = useState<modalKey>();
  const [isExpanded, setIsExpanded] = useState(false);
  const closeModal = () => setModalKey(undefined);
  const openModal = (modal: modalKey) => setModalKey(modal);

  const expiringCreditsCount = useMemo(() => getExpiringSoonCreditsCount(credits), [credits]);
  const creditBreakdown = useMemo(() => getCreditBreakdown(credits.activeAllocations || []), [credits]);
  const hasAvailableCredits = hasCredits(credits);
  const shouldShowCreditDetails = hasAvailableCredits && isExpanded;

  if (isNewAccountDetailsPageEnabled) {
    return (
      <YStack gap="sm">
        <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="lg" p="md" radius="lg">
          <XStack alignItems="start" justifyContent="between">
            <XStack alignItems="start" gap="md">
              <YStack gap="xs">
                <YStack
                  centered
                  p="sm"
                  radius="lg"
                  style={{
                    background: 'linear-gradient(135deg, #A2DBC6 21.18%, #E5EDF7 52.8%, #EEFB86 89.37%)',
                    minHeight: 56,
                    minWidth: 56,
                  }}
                >
                  <CoinsIcon />
                </YStack>
                <Text color="secondary" type="p-md">
                  {pricingTerminology.balance_title}
                </Text>
                {showCredits ? (
                  <Text color="primary" type="h1">
                    {credits.availableCredits}
                  </Text>
                ) : null}
                {showCredits && expiringCreditsCount > 0 ? (
                  <XStack alignItems="center" gap="sm">
                    <Icon color="warning" name="IconCircle" size={8} />
                    <Text color="secondary" type="p-sm">
                      {expiringCreditsCount} {pluralize(pricingTerminology.unit_term, expiringCreditsCount)} expire soon
                    </Text>
                  </XStack>
                ) : null}
              </YStack>
            </XStack>
            {onAccountPress ? <Button label="Your account" onClick={onAccountPress} variant="secondary" /> : null}
          </XStack>
        </YStack>
      </YStack>
    );
  }

  const lastRenewalDate = credits.activeAllocations?.[0]?.createdAt;
  const totalCredits = credits.activeAllocations?.[0]?.amount || 0;

  return (
    <YStack gap="sm">
      <Text type="h4">{`Your ${capitalize(pricingTerminology.unit_term)}s`}</Text>
      <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="md" p="md" radius="lg">
        <YStack gap="md">
          <XStack alignItems="center" gap="lg">
            {showCredits ? (
              <XStack
                justifyContent="center"
                radius="lg"
                style={{
                  background: 'linear-gradient(135deg, #A2DBC6 21.18%, #E5EDF7 52.8%, #EEFB86 89.37%)',
                }}
              >
                <YStack centered p="sm" radius="lg" style={{ minHeight: 56, minWidth: 56 }}>
                  <CoinsIcon />
                </YStack>
              </XStack>
            ) : null}
            <YStack gap="xs">
              <Text color="primary" type="h3">
                {showCredits
                  ? `${credits.availableCredits} ${pluralize(pricingTerminology.unit_term, credits.availableCredits)} available`
                  : pricingTerminology.balance_title}
              </Text>
              {showCredits && totalCredits && lastRenewalDate ? (
                <Text color="secondary" type="p-md">
                  {`${totalCredits} ${pluralize(pricingTerminology.unit_term, totalCredits)} added on ${dateFormatters['MMM D'](lastRenewalDate)}`}
                </Text>
              ) : null}
            </YStack>
          </XStack>
          <XStack alignItems="center" gap="md">
            <Button label={pricingTerminology.history_title} onClick={() => openModal('history')} variant="secondary" />
            {canBuyCredits({ client, clientStatus, credits }) ? (
              <Button label={pricingTerminology.add_button} onClick={() => openModal('buy')} />
            ) : null}
            {showCredits && hasAvailableCredits ? (
              <Button
                aria-label={
                  isExpanded
                    ? `Hide ${pricingTerminology.unit_term} details`
                    : `Show ${pricingTerminology.unit_term} details`
                }
                onClick={() => setIsExpanded(!isExpanded)}
                size="md"
                variant="secondary"
              >
                <Icon color="primary" name={isExpanded ? 'IconChevronUp' : 'IconChevronDown'} />
              </Button>
            ) : null}
          </XStack>
        </YStack>

        {shouldShowCreditDetails ? (
          <YStack borderColor="borderSecondary" borderTop gap="md" pt="md">
            <Text color="secondary" type="p-md">
              {`${capitalize(pricingTerminology.unit_term)} details`}
            </Text>
            <YStack>
              {creditBreakdown.map((item, index) => {
                const isLastItem = index === creditBreakdown.length - 1;
                return (
                  <XStack
                    alignItems="center"
                    borderBottom={!isLastItem}
                    borderColor={!isLastItem ? 'neutral200' : undefined}
                    justifyContent="between"
                    key={item.allocationId}
                    py="sm"
                  >
                    <XStack alignItems="center" flex={1} gap="md">
                      <Icon
                        className="cursor-default"
                        color="secondary"
                        name={creditTypeConfig[item.creditType]?.icon}
                        size={20}
                      />
                      <Text type="p-md">{creditTypeConfig[item.creditType]?.label ?? item.creditType}</Text>
                    </XStack>
                    <XStack alignItems="center" gap="lg">
                      <Text type="p-md">{item.remainingCredits}</Text>
                      <Text color="secondary" style={{ minWidth: 100 }} textAlign="end" type="p-sm">
                        Expire {item.expiration ? dateFormatters['M/D'](item.expiration) : 'never'}
                      </Text>
                    </XStack>
                  </XStack>
                );
              })}
            </YStack>
          </YStack>
        ) : null}
      </YStack>
      <CreditHistoryModal
        closeModal={closeModal}
        isError={ledgerError}
        isLoading={loadingLedger}
        isOpen={modalKey === 'history'}
        ledgerEntries={ledgerEntries}
        pricingTerminology={pricingTerminology}
        subscriptionRenewalCredits={credits.subscriptionRenewalCredits}
      />
      <BuyCreditsModal
        closeModal={closeModal}
        isOpen={modalKey === 'buy'}
        onCreditsPurchased={() => {
          onCreditsRefetch?.();
          refetchLedger();
        }}
      />
    </YStack>
  );
};
